// src/meeting/forms/InvitationFinancialApprovalForm.jsx
import React, { useEffect, useMemo, useState } from "react";
import PrintField from "../../shared/PrintField";
import "./InvitationtotheFinancialApprovalMeeting.css";

function splitLongPlace(value) {
  const text = String(value || "").trim();
  if (text.length <= 42) return [text, ""];

  const thaiAddressBreak = text.search(/\s?(ต\.|แขวง)/);
  if (thaiAddressBreak > 10) {
    return [
      text.slice(0, thaiAddressBreak).trim(),
      text.slice(thaiAddressBreak).trim(),
    ];
  }

  const breakAt = text.lastIndexOf(" ", 42);
  const index = breakAt > 18 ? breakAt : 42;
  return [text.slice(0, index).trim(), text.slice(index).trim()];
}

// ✅ ทำเป็น forwardRef เพื่อรับ ref จาก FormWrapper/MeetingFormWrapper
const InvitationFinancialApprovalForm = React.forwardRef((props, ref) => {
  const { isCapturing = false, isPrintMode = false, initialData = null } = props;

  // ====== form state (เก็บค่าทุกช่อง) ======
  const [data, setData] = useState({
    company: "",
    dateDay: "",
    dateMonth: "",
    dateYear: "",

    subjectMeetingNo1: "",
    subjectMeetingNo2: "",

    bodyMeetingNo1: "",
    bodyMeetingNo2: "",

    meetingDay: "",
    meetingMonth: "",
    meetingYear: "",
    meetingTime: "",
    meetingPlace: "",
    meetingPlace2: "",

    agenda1No1: "",
    agenda1No2: "",

    agenda3AtDate: "",

    signName: "",
    signPosition: "",
  });

  useEffect(() => {
    if (!initialData) return;
    const [meetingPlaceLine1, meetingPlaceLine2] = splitLongPlace(initialData.meetingPlace);

    setData((prev) => ({
      ...prev,
      ...initialData,
      company: initialData.company ?? prev.company,
      dateDay: initialData.dateDay ?? prev.dateDay,
      dateMonth: initialData.dateMonth ?? prev.dateMonth,
      dateYear: initialData.dateYear ?? prev.dateYear,
      meetingDay: initialData.meetingDay ?? prev.meetingDay,
      meetingMonth: initialData.meetingMonth ?? prev.meetingMonth,
      meetingYear: initialData.meetingYear ?? prev.meetingYear,
      meetingTime: initialData.meetingTime ?? prev.meetingTime,
      meetingPlace: initialData.meetingPlaceLine1 ?? meetingPlaceLine1 ?? prev.meetingPlace,
      meetingPlace2: initialData.meetingPlace2 ?? initialData.meetingPlaceLine2 ?? meetingPlaceLine2 ?? prev.meetingPlace2,
      subjectMeetingNo1: initialData.subjectMeetingNo1 ?? initialData.meetingNo ?? prev.subjectMeetingNo1,
      bodyMeetingNo1: initialData.bodyMeetingNo1 ?? initialData.meetingNo ?? prev.bodyMeetingNo1,
      agenda1No1: initialData.agenda1No1 ?? initialData.meetingNo ?? prev.agenda1No1,
      signName: initialData.signName ?? prev.signName,
      signPosition: initialData.signPosition ?? prev.signPosition,
    }));
  }, [initialData]);

  const setField = (key) => (nextValue) => {
    const value =
      nextValue && typeof nextValue === "object" && "target" in nextValue
        ? nextValue.target?.value
        : nextValue;

    setData((prev) => ({ ...prev, [key]: value ?? "" }));
  };
  const printFieldProps = { isCapturing, isPrintMode };

  // สไตล์ที่ใช้ซ้ำ (เพื่อให้ขนาดนิ่ง)
  const styles = useMemo(
    () => ({
      company: { width: "320px" },
      day: { width: "50px" },
      month: { width: "110px" },
      year: { width: "80px" },
      meetingYear: { width: "140px" },
      tiny: { width: "40px" },
      date40: { width: "40px" },
      date70: { width: "110px" },
      time50: { width: "70px" },
      place140: { width: "285px" },
      placeLine2: { width: "430px" },
      sign200: { width: "200px" },
    }),
    []
  );

  return (
    <div className="inv-page">
      {/* ✅ เอา ref มาเกาะที่กระดาษ A4 */}
      <div className="inv-paper ordinary-meeting-form" ref={ref}>
        {/* ส่วนหัวขวา */}
        <div className="inv-header-right">
          <div className="inv-bold">
            บริษัท{" "}
            <PrintField
              {...printFieldProps}
              value={data.company}
              onChange={setField("company")}
              className="inv-line inv-line-long ordinary-right-field"
              style={styles.company}
            />
          </div>

          <div className="inv-header-date inv-bold">
            วันที่{" "}
            <PrintField
              {...printFieldProps}
              value={data.dateDay}
              onChange={setField("dateDay")}
              className="inv-line inv-line-short ordinary-right-field"
              style={styles.day}
            />
            เดือน{" "}
            <PrintField
              {...printFieldProps}
              value={data.dateMonth}
              onChange={setField("dateMonth")}
              className="inv-line inv-line-short ordinary-right-field"
              style={styles.month}
            />
            ปี{" "}
            <PrintField
              {...printFieldProps}
              value={data.dateYear}
              onChange={setField("dateYear")}
              className="inv-line inv-line-short ordinary-right-field"
              style={styles.year}
            />
          </div>
        </div>

        {/* เนื้อหา */}
        <div className="inv-body">
          <div className="inv-row">
            <span className="inv-bold">
              เรื่อง&nbsp;&nbsp;ขอเชิญประชุมสามัญผู้ถือหุ้นประจำปี ครั้งที่
            </span>{" "}
            <PrintField
              {...printFieldProps}
              value={data.subjectMeetingNo1}
              onChange={setField("subjectMeetingNo1")}
              className="inv-line inv-line-tiny ordinary-right-field"
              style={styles.tiny}
            />{" "}
            /
            <PrintField
              {...printFieldProps}
              value={data.subjectMeetingNo2}
              onChange={setField("subjectMeetingNo2")}
              className="inv-line inv-line-tiny ordinary-right-field"
              style={styles.tiny}
            />
          </div>

          <div className="inv-row inv-row-learn inv-bold">
            เรียน&nbsp;&nbsp;ท่านผู้ถือหุ้น
          </div>

          <p className="inv-paragraph">
            ด้วยที่ประชุมคณะกรรมการบริษัท มีมติให้เรียกประชุมสามัญผู้ถือหุ้นประจำปี
            <br />
            <span className="inv-meeting-meta">
              <span className="inv-field-group">
                ครั้งที่{" "}
                <PrintField
                  {...printFieldProps}
                  value={data.bodyMeetingNo1}
                  onChange={setField("bodyMeetingNo1")}
                  className="inv-line inv-line-tiny ordinary-right-field"
                  style={styles.tiny}
                />
                /
                <PrintField
                  {...printFieldProps}
                  value={data.bodyMeetingNo2}
                  onChange={setField("bodyMeetingNo2")}
                  className="inv-line inv-line-tiny ordinary-right-field"
                  style={styles.tiny}
                />
              </span>
              <span className="inv-field-group">
                ในวันที่{" "}
                <PrintField
                  {...printFieldProps}
                  value={data.meetingDay}
                  onChange={setField("meetingDay")}
                  className="inv-line inv-line-date ordinary-right-field"
                  style={styles.date40}
                />
              </span>
              <span className="inv-field-group">
                เดือน{" "}
                <PrintField
                  {...printFieldProps}
                  value={data.meetingMonth}
                  onChange={setField("meetingMonth")}
                  className="inv-line inv-line-date ordinary-right-field"
                  style={styles.date70}
                />
              </span>
              <span className="inv-field-group">
                ปี{" "}
                <PrintField
                  {...printFieldProps}
                  value={data.meetingYear}
                  onChange={setField("meetingYear")}
                  className="inv-line inv-line-date ordinary-right-field"
                  style={styles.meetingYear}
                />
              </span>
              <span className="inv-field-group">
                เวลา{" "}
                <PrintField
                  {...printFieldProps}
                  value={data.meetingTime}
                  onChange={setField("meetingTime")}
                  className="inv-line inv-line-date ordinary-right-field"
                  style={styles.time50}
                />{" "}
                น.
              </span>
              <span className="inv-field-group inv-place-field-group">
                ณ สำนักงานของบริษัท{" "}
                <PrintField
                  {...printFieldProps}
                  value={data.meetingPlace}
                  onChange={setField("meetingPlace")}
                  className="inv-line inv-line-long ordinary-right-field"
                  style={styles.place140}
                />
              </span>
            </span>
            {/* address line 2: on the printed / captured paper an empty line is left out and a filled one takes only
                its text width, so "โดยมีระเบียบวาระการประชุมดังนี้ คือ" does not wrap after a 430px blank */}
            {(!(isCapturing || isPrintMode) || String(data.meetingPlace2 || "").trim() !== "") && (
              <span className="inv-place-row">
                <br />
                <PrintField
                  {...printFieldProps}
                  printFit
                  value={data.meetingPlace2}
                  onChange={setField("meetingPlace2")}
                  className="inv-line inv-line-long ordinary-right-field"
                  style={styles.placeLine2}
                />{" "}
              </span>
            )}
            โดยมีระเบียบวาระการประชุมดังนี้ คือ
          </p>

          <div className="inv-agenda">
            <div className="inv-agenda-item">
              วาระที่ 1 พิจารณารับรองรายงานการประชุมสามัญผู้ถือหุ้น ครั้งที่{" "}
              <PrintField
                {...printFieldProps}
                value={data.agenda1No1}
                onChange={setField("agenda1No1")}
                className="inv-line inv-line-tiny ordinary-right-field"
                style={styles.tiny}
              />
              /
              <PrintField
                {...printFieldProps}
                value={data.agenda1No2}
                onChange={setField("agenda1No2")}
                className="inv-line inv-line-tiny ordinary-right-field"
                style={styles.tiny}
              />
            </div>

            <div className="inv-agenda-item">วาระที่ 2 พิจารณาผลการดำเนินงานของบริษัท</div>

            <div className="inv-agenda-item">
              วาระที่ 3 พิจารณาอนุมัติงบแสดงฐานะการเงินและงบกำไรขาดทุน รอบปีบัญชีสิ้นสุด
            </div>

            <div className="inv-agenda-sub">
              ณ วันที่{" "}
              <PrintField
                {...printFieldProps}
                value={data.agenda3AtDate}
                onChange={setField("agenda3AtDate")}
                className="inv-line inv-line-date ordinary-right-field"
                style={{ width: "160px" }}
              />
            </div>

            <div className="inv-agenda-item">
              วาระที่ 4 พิจารณาแต่งตั้งผู้สอบบัญชี และกำหนดค่าตอบแทน
            </div>

            <div className="inv-agenda-item">
              วาระที่ 5 พิจารณาแต่งตั้งคณะกรรมการและกำหนดค่าตอบแทนคณะกรรมการ
            </div>

            <div className="inv-agenda-item">วาระที่ 6 พิจารณาเรื่องอื่น ๆ (ถ้ามี)</div>
          </div>

          <p className="inv-paragraph">
            ดังนั้น จึงขอเรียนเชิญท่านผู้ถือหุ้นเข้าประชุมในวัน เวลา และสถานที่ดังกล่าวข้างต้น
            หากท่านไม่สามารถจะไปประชุมด้วยตนเองได้ โปรดทำหนังสือมอบฉันทะ เพื่อมอบอำนาจให้ผู้อื่นไปประชุมแทนด้วย
            จักขอบคุณยิ่ง
          </p>

          <div className="inv-footer">
            <div className="inv-footer-text">ขอแสดงความนับถือ</div>

            <div className="inv-sign-block">
              (
              <PrintField
                {...printFieldProps}
                value={data.signName}
                onChange={setField("signName")}
                className="inv-line inv-line-long-extra ordinary-center-field"
                style={styles.sign200}
              />
              )
              <div>
                <PrintField
                  {...printFieldProps}
                  value={data.signPosition}
                  onChange={setField("signPosition")}
                  className="inv-line inv-line-long ordinary-center-field"
                  style={styles.sign200}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default InvitationFinancialApprovalForm;

